import * as React from "react";
import "./Uploader.scss";
import { Button, Dropdown, Menu, Upload } from "antd";
import { useEffect, useState } from "react";
import { ReactNode } from "react";

import { EditOutlined } from "@ant-design/icons";
import { handleServerError } from "../../../util/ErrorHandler";
import { ALLOWED_IMAGE_TYPES, MAX_IMAGE_SIZE } from "../../../util/constants";
import { getImagePath, getSizeInMB } from "../../../util/utils";
import BrandsApi from "../../../api/BrandsApi";
import FileTooBigModal from "./FileTooBigModal";
import FileInvalidTypeModal from "./FileInvalidTypeModal";

export enum ImageUploaderTypeEnum {
  Button,
  Card,
  Circle,
}

export enum UploadTypeEnum {
  Logo,
  BackgroundImage,
}

interface UploaderProps {
  uploadType: UploadTypeEnum;
  brandId: string;
  widgetType: ImageUploaderTypeEnum;
  onUploadSuccess: (path: any) => void;
  addLabel: ReactNode;
  changeLabel?: ReactNode;
  removeItemImage?: () => void;
  path?: string;
  className?: string;
  hidePreview?: boolean;
  hasDropdown?: boolean;
}

export function Uploader({
  uploadType,
  brandId,
  widgetType,
  path,
  addLabel,
  changeLabel,
  className,
  hidePreview,
  hasDropdown = false,
  removeItemImage,
  onUploadSuccess,
}: UploaderProps) {
  const [preview, setPreview] = useState(path);
  const [loading, setLoading] = useState(false);
  const [showFileTooBigModal, setShowFileTooBigModal] = useState(false);
  const [showFileInvalidModal, setShowFileInvalidModal] = useState(false);

  useEffect(() => {
    setPreview(path);
  }, [path]);

  const uploadFile = (file: File) => {
    setLoading(true);

    if (uploadType === UploadTypeEnum.Logo) {
      BrandsApi.uploadLogo(brandId, file)
        .then((resp) => {
          onUploadSuccess(resp.data);
          setPreview(resp.data.path);
          setLoading(false);
        })
        .catch((err) => {
          handleServerError(err);
          setLoading(false);
        });
    } else if (uploadType === UploadTypeEnum.BackgroundImage) {
      BrandsApi.uploadBackgroundImage(brandId, file)
        .then((resp) => {
          onUploadSuccess(resp.data);
          setPreview(resp.data.path);
          setLoading(false);
        })
        .catch((err) => {
          handleServerError(err);
          setLoading(false);
        });
    }
  };

  const beforeUpload = (file: File) => {
    const filename = file.name.toLowerCase();
    if (
      (!file.type &&
        !filename.endsWith(".jpg") &&
        !filename.endsWith(".png")) ||
      ALLOWED_IMAGE_TYPES.indexOf(file.type) < 0
    ) {
      setShowFileInvalidModal(true);
      return false;
    }

    if (getSizeInMB(file) >= MAX_IMAGE_SIZE) {
      setShowFileTooBigModal(true);
    } else {
      uploadFile(file);
    }
    return false;
  };

  const toggleDropdown = (e: any) => {
    if (+e.key === 1) {
      if (removeItemImage) removeItemImage();
    }
  };

  const menu = (
    <Menu className={"upload-img-menu"} onClick={toggleDropdown}>
      <Menu.Item key="0">
        <Upload
          beforeUpload={beforeUpload}
          accept={ALLOWED_IMAGE_TYPES}
          showUploadList={false}
        >
          Replace Photo
        </Upload>
      </Menu.Item>
      <Menu.Item key="1">Delete Photo</Menu.Item>
    </Menu>
  );

  return (
    <div
      className={`uploader ${hasDropdown ? "with-drop" : ""} ${
        className || ""
      }`}
    >
      {!hidePreview && preview && (
        <div
          className="preview"
          style={{ backgroundImage: `url(${getImagePath(preview)})` }}
        />
      )}

      {hasDropdown && preview && (
        <Dropdown overlay={menu} trigger={["click"]}>
          <Button type={"link"} loading={loading}>
            {!loading && <EditOutlined />}
          </Button>
        </Dropdown>
      )}

      {(!hasDropdown || !preview) && (
        <Upload
          className={!preview ? "no" : ""}
          listType={
            widgetType === ImageUploaderTypeEnum.Card ? "picture-card" : "text"
          }
          beforeUpload={beforeUpload}
          accept={ALLOWED_IMAGE_TYPES}
          showUploadList={false}
        >
          <Button
            type={widgetType === ImageUploaderTypeEnum.Card ? "link" : "text"}
            loading={loading}
          >
            {!loading && (preview ? changeLabel || addLabel : addLabel)}
          </Button>
        </Upload>
      )}
      {showFileTooBigModal && (
        <FileTooBigModal
          onOk={() => setShowFileTooBigModal(false)}
          maxSize={MAX_IMAGE_SIZE}
        />
      )}
      {showFileInvalidModal && (
        <FileInvalidTypeModal onOk={() => setShowFileInvalidModal(false)} />
      )}
    </div>
  );
}
